// src/config/app.config.ts

/**
 * Application Configuration
 * Core application settings and constants
 */

interface AppConfig {
  name: string;
  version: string;
  environment: 'development' | 'staging' | 'production';
  debug: boolean;
  
  // URLs
  baseUrl: string;
  apiUrl: string;
  cdnUrl: string;
  
  // App settings
  defaultLanguage: string;
  supportedLanguages: string[];
  timezone: string;
  dateFormat: string;
  timeFormat: string;
  
  // UI settings
  theme: {
    default: 'light' | 'dark' | 'system';
    available: string[];
  };
  
  // Pagination
  pagination: {
    defaultPageSize: number;
    pageSizeOptions: number[];
    maxPageSize: number;
  };
  
  // Session
  session: {
    timeout: number;
    warningTime: number;
    checkInterval: number;
  };
  
  // Storage
  storage: {
    prefix: string;
    encryptionEnabled: boolean;
  };
  
  // Performance
  performance: {
    enablePrefetch: boolean;
    enableLazyLoading: boolean;
    chunkSize: number;
  };
}

export const appConfig: AppConfig = {
  name: import.meta.env.VITE_APP_NAME || 'HR System',
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  environment: (import.meta.env.VITE_APP_ENV as AppConfig['environment']) || 'development',
  debug: import.meta.env.VITE_DEBUG === 'true' || import.meta.env.DEV,
  
  // URLs
  baseUrl: import.meta.env.VITE_APP_URL || 'http://localhost:5173',
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8001',
  cdnUrl: import.meta.env.VITE_CDN_URL || '',
  
  // App settings
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'es', 'fr', 'de', 'pt', 'ja', 'zh'],
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h', // '12h' or '24h'
  
  // UI settings
  theme: {
    default: 'system',
    available: ['light', 'dark', 'system'],
  },
  
  // Pagination
  pagination: {
    defaultPageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
    maxPageSize: 100,
  },
  
  // Session
  session: {
    timeout: 30 * 60 * 1000, // 30 minutes
    warningTime: 5 * 60 * 1000, // 5 minutes before timeout
    checkInterval: 60 * 1000, // Check every minute
  },
  
  // Storage
  storage: {
    prefix: 'hr_system_',
    encryptionEnabled: true,
  },
  
  // Performance
  performance: {
    enablePrefetch: true,
    enableLazyLoading: true,
    chunkSize: 50,
  },
};

// Domain configuration
export const domainConfig = {
  main: import.meta.env.VITE_APP_DOMAIN || 'hrsystem.com',
  app: import.meta.env.VITE_APP_SUBDOMAIN || 'app',
  api: import.meta.env.VITE_API_SUBDOMAIN || 'api',
  cdn: import.meta.env.VITE_CDN_SUBDOMAIN || 'cdn',
  admin: import.meta.env.VITE_ADMIN_SUBDOMAIN || 'admin',
  
  // Cookie domain for subdomain sharing
  cookieDomain: import.meta.env.VITE_COOKIE_DOMAIN || '.hrsystem.com',
  
  // Subdomain patterns
  patterns: {
    tenant: /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/,
    reserved: ['www', 'app', 'api', 'cdn', 'admin', 'dashboard', 'login', 'register', 'help', 'support', 'docs'],
  },
};

// Admin Portal Configuration
export const adminPortalConfig = {
  // URLs for different environments
  urls: {
    development: import.meta.env.VITE_ADMIN_PORTAL_URL || 'http://localhost:3001',
    staging: import.meta.env.VITE_ADMIN_PORTAL_STAGING_URL || 'https://admin-staging.amashr.com',
    production: import.meta.env.VITE_ADMIN_PORTAL_PROD_URL || 'https://admin.amashr.com',
  },
  
  // Get the current admin portal URL based on environment
  getUrl: () => {
    const env = appConfig.environment;
    return adminPortalConfig.urls[env] || adminPortalConfig.urls.development;
  },
  
  // Redirect to admin portal with authentication token
  redirect: (userRole?: string, token?: string) => {
    const adminUrl = adminPortalConfig.getUrl();
    const params = new URLSearchParams();
    
    if (token) {
      params.append('token', token);
    }
    
    if (userRole) {
      params.append('role', userRole);
    }
    
    const queryString = params.toString();
    const redirectUrl = queryString ? `${adminUrl}?${queryString}` : adminUrl;
    
    // Use window.location for cross-origin navigation
    window.location.href = redirectUrl;
  }
};

// Navigation configuration
export const navigationConfig = {
  main: [
    { name: 'Dashboard', path: '/dashboard', icon: 'Building2' },
    { name: 'Employees', path: '/employees', icon: 'Users' },
    { name: 'Leave', path: '/leave', icon: 'Calendar' },
    { name: 'Payroll', path: '/payroll', icon: 'DollarSign' },
    { name: 'Reports', path: '/reports', icon: 'FileText' },
    { name: 'Settings', path: '/settings', icon: 'Settings' },
  ],
  
  userMenu: [
    { name: 'Profile', path: '/profile', icon: 'User' },
    { name: 'Preferences', path: '/preferences', icon: 'Sliders' },
    { name: 'Help', path: '/help', icon: 'HelpCircle' },
  ],
};

// External services configuration
export const servicesConfig = {
  analytics: {
    enabled: import.meta.env.VITE_ANALYTICS_ENABLED === 'true',
    googleAnalyticsId: import.meta.env.VITE_GA_ID,
    mixpanelToken: import.meta.env.VITE_MIXPANEL_TOKEN,
  },
  
  sentry: {
    enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV,
    tracesSampleRate: 0.1,
  },
  
  intercom: {
    enabled: import.meta.env.VITE_INTERCOM_ENABLED === 'true',
    appId: import.meta.env.VITE_INTERCOM_APP_ID,
  },
  
  stripe: {
    enabled: import.meta.env.VITE_STRIPE_ENABLED === 'true',
    publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  },
};

// Email configuration
export const emailConfig = {
  fromAddress: 'noreply@hrsystem.com',
  fromName: 'HR System',
  supportEmail: 'support@hrsystem.com',
  templates: {
    welcome: 'welcome',
    passwordReset: 'password-reset',
    invitation: 'invitation',
    verification: 'email-verification',
  },
};

// Notification settings
export const notificationConfig = {
  position: 'top-right' as const,
  duration: 5000,
  maxVisible: 3,
  sounds: {
    enabled: true,
    success: '/sounds/success.mp3',
    error: '/sounds/error.mp3',
    warning: '/sounds/warning.mp3',
    info: '/sounds/info.mp3',
  },
};

// Validation rules
export const validationConfig = {
  password: {
    minLength: 8,
    maxLength: 128,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
  },
  
  username: {
    minLength: 3,
    maxLength: 30,
    pattern: /^[a-zA-Z0-9_-]+$/,
  },
  
  workspace: {
    minLength: 3,
    maxLength: 63,
    pattern: /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/,
  },
};

export default appConfig;